import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner } from 'react-bootstrap'
import { itemFamilyAPI } from '../api/itemFamilyAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'
import ConfirmationModal from '../components/ConfirmationModal'

function ItemFamilyListScreen() {
    const { hasPermission } = usePermissions()
    const [itemFamilies, setItemFamilies] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState(null)

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('itemFamilyColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            name: 'auto',
            items_count: 150,
            actions: 150
        };
    });

    useEffect(() => {
        localStorage.setItem('itemFamilyColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchItemFamilies() {
            try {
                setLoading(true)
                const response = await itemFamilyAPI.getAll(currentPage, pageSize, searchTerm)
                setItemFamilies(response.results)
                setTotalPages(response.count)
            } finally {
                setLoading(false)
            }
        }
        fetchItemFamilies()
    }, [currentPage, pageSize, searchTerm])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handleDeleteClick = (e, family) => {
        e.stopPropagation();
        setItemToDelete(family);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                await itemFamilyAPI.delete(itemToDelete._id);
                setItemFamilies(itemFamilies.filter(f => f._id !== itemToDelete._id));
            } catch (error) {
                console.error('Error deleting item family:', error);
                alert(`Failed to delete item family: ${error.response?.data?.message || error.message}`);
            } finally {
                setShowDeleteModal(false);
                setItemToDelete(null);
            }
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Item Families</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search item families..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    {hasPermission('stock.items.modify') && (
                        <Button
                            onClick={() => navigate('/item-family/create')}
                            variant="success"
                        >
                            Create Item Family
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="item_families_table"
                        columns={[
                            {
                                id: 'name',
                                header: 'Name',
                                cell: (row) => highlightText(row.name, searchTerm)
                            },
                            {
                                id: 'items_count',
                                header: 'Items Count',
                                cell: (row) => row.items_count
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    hasPermission('stock.items.modify') && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={(e) => handleDeleteClick(e, row)}
                                        >
                                            Delete
                                        </Button>
                                    )
                                )
                            }
                        ]}
                        data={itemFamilies}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/item-family/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the item family "${itemToDelete?.name}"? Stock items in this family will be unassigned.`}
            />
        </Container>
    )
}

export default ItemFamilyListScreen
